import type { ReactNode, ElementType } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@ui/card.tsx';
import CountUp from 'react-countup';
import { DataBrickSkeleton } from '@r/components/kpi/skeleton/data-brick-skeleton.tsx';
import { cn } from '@r/utils/utils.ts';

export interface StatBrickProps {
  title?: string | null;
  icon?: ReactNode | null;
  value?: number | string | null;
  valueSuffix?: string | null;
  valueClassName?: string;
  info?: string | null;
  className?: string;
  decimals?: 0 | 2;
  loading?: boolean;
  as?: ElementType;
  cardMode?: boolean;
  hasValidData?: boolean;
}

export function StatBrick({
  title = '',
  icon = null,
  value = null,
  valueSuffix = null,
  valueClassName = 'text-2xl',
  info = null,
  className = '',
  decimals = 0,
  loading = false,
  as,
  cardMode = true,
  hasValidData = false
}: StatBrickProps) {
  if (loading || !hasValidData) {
    return <DataBrickSkeleton size="small" />;
  }

  const Wrapper = as ?? (cardMode ? Card : 'div');
  const Header = cardMode ? CardHeader : 'div';
  const Content = cardMode ? CardContent : 'div';

  return (
    <Wrapper
      className={cn('flex flex-col justify-between grow', className, !cardMode && 'space-y-6')}
    >
      <Header>
        <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
          {icon}
          {title && <div className="text-xl font-bold">{title}</div>}
        </CardTitle>
      </Header>

      <Content className="flex flex-col font-regular">
        <div className={cn(valueClassName, 'font-bold')}>
          {typeof value === 'number' ? (
            <CountUp
              className={cn(valueClassName, 'font-bold')}
              end={value}
              duration={2}
              suffix={valueSuffix ? ` ${valueSuffix}` : undefined}
              separator={' '}
              decimals={decimals}
            />
          ) : (
            value
          )}
        </div>

        {info && <div className="mt-2 text-sm font-light text-muted-foreground">{info}</div>}
      </Content>
    </Wrapper>
  );
}
